// src/SQLFirestore.js
import { Parser } from 'node-sql-parser';
import { 
  collection, 
  query as firestoreQuery, 
  where as firestoreWhere, 
  orderBy as firestoreOrderBy, 
  limit as firestoreLimit, 
  getDocs 
} from 'firebase/firestore';
import { firestore } from './firebase'; // Ensure Firestore is initialized correctly

class SQLFirestore {
  constructor() {
    this.parser = new Parser();
    this.selectedFields = null; // To store fields specified in SELECT
    this.orConditionGroups = []; // To store OR condition groups
    this.selectFunctions = []; // To store functions like CONCAT in SELECT
    this.selectAggregations = []; // To store aggregation functions in SELECT

    // Define field type mappings
    this.fieldTypes = {
      fechaNacimiento: 'date',
      edad: 'number',
      nombre: 'string',
      apellido: 'string',
      ciudad: 'string',
      // Add other fields and their types as needed
    };
  }

  /**
   * Helper method to extract function name safely.
   * @param {string|object} name - The function name, either as a string or an object.
   * @returns {string} - The uppercase function name.
   */
  extractFunctionName(name) {
    if (typeof name === 'string') {
      return name.toUpperCase();
    } else if (typeof name === 'object' && name !== null) {
      // Assuming the structure has a 'name' array with parts
      if (Array.isArray(name.name)) {
        return name.name.map(part => part.value).join('.').toUpperCase();
      } else if (typeof name.value === 'string') {
        return name.value.toUpperCase();
      }
    }
    throw new Error('Unsupported function name format in SELECT clause.');
  }

  /**
   * Parses the SQL query into an Abstract Syntax Tree (AST).
   * @param {string} sql - The SQL query string.
   * @returns {object} - The AST of the SQL query.
   */
  parseSQL(sql) {
    try {
      const ast = this.parser.astify(sql);
      console.log('Parsed SQL AST:', JSON.stringify(ast, null, 2));
      return ast;
    } catch (error) {
      throw new Error('Invalid SQL Query');
    }
  }

  /**
   * Determines if the query contains any aggregate functions.
   * @param {object} ast - The AST of the SQL query.
   * @returns {boolean} - True if aggregate functions are present, else false.
   */
  hasAggregation(ast) {
    if (ast.type !== 'select') return false;
    return ast.columns.some(col => 
      col.expr && 
      (col.expr.type === 'aggr_func' || col.expr.type === 'aggregate_func')
    );
  }

  /**
   * Translates the AST into Firestore query constraints.
   * Handles OR logic by splitting into condition groups.
   * @param {object} ast - The AST of the SQL query.
   * @returns {object} - An object containing Firestore reference, condition groups, common constraints, and aggregation flag.
   */
  translateToFirestore(ast) {
    if (ast.type !== 'select') {
      throw new Error('Only SELECT statements are supported');
    }

    const from = ast.from[0].table;
    const firestoreRef = collection(firestore, from);

    // Handle WHERE clauses
    let conditionGroups = [[]];
    if (ast.where) {
      conditionGroups = this.parseWhere(ast.where);
    }

    // Handle ORDER BY and LIMIT if applicable (only for individual queries)
    const commonConstraints = [];

    if (ast.orderby) {
      const orderConstraints = this.parseOrderBy(ast.orderby);
      commonConstraints.push(...orderConstraints);
    }

    if (ast.limit) {
      const limitConstraint = this.parseLimit(ast.limit);
      commonConstraints.push(limitConstraint);
    }

    // Determine if the query contains aggregations
    const hasAggregation = this.hasAggregation(ast);

    // Handle SELECT columns (Projections, Functions, and Aggregations)
    if (ast.columns && ast.columns.length > 0) {
      const selectedFields = [];
      this.selectFunctions = []; // Reset selectFunctions
      this.selectAggregations = []; // Reset selectAggregations

      ast.columns.forEach(col => {
        if (col.expr.type === 'column_ref') {
          // Regular column reference
          selectedFields.push(col.expr.column);
        } else if (col.expr.type === 'function') {
          // Function in SELECT (e.g., CONCAT)
          const funcName = this.extractFunctionName(col.expr.name);
          if (funcName === 'CONCAT') {
            // Extract arguments
            const args = col.expr.args.value.map(arg => {
              if (arg.type === 'column_ref') {
                return { type: 'field', value: arg.column };
              } else if (arg.type === 'string' || arg.type.endsWith('_string')) {
                // Handle different string literal types
                return { type: 'literal', value: arg.value };
              } else {
                throw new Error(`Unsupported argument type in CONCAT: ${arg.type}`);
              }
            });

            // Extract alias
            const alias = col.as || `${funcName.toLowerCase()}_${args.map(a => a.value).join('_')}`;

            // Store the function details for later processing
            this.selectFunctions.push({
              function: funcName,
              args,
              alias
            });

            // Collect field names required for the function
            args.forEach(arg => {
              if (arg.type === 'field') {
                selectedFields.push(arg.value);
              }
            });
          } else {
            throw new Error(`Unsupported function in SELECT clause: ${funcName}`);
          }
        } else if (col.expr.type === 'aggr_func' || col.expr.type === 'aggregate_func') {
          // Handle aggregation functions (e.g., COUNT, SUM, AVG)
          const aggFuncName = this.extractFunctionName(col.expr.name);
          // Safely extract aggregation field
          let aggField = '*';
          if (col.expr.args && col.expr.args.expr && col.expr.args.expr.type === 'star') {
            aggField = '*';
          } else if (col.expr.args && col.expr.args.expr && col.expr.args.expr.type === 'column_ref') {
            aggField = col.expr.args.expr.column;
          } else {
            throw new Error(`Unsupported argument type in ${aggFuncName}: ${col.expr.args.expr.type}`);
          }

          // Extract alias
          const alias = col.as || `${aggFuncName.toLowerCase()}_${aggField}`;

          // Store the aggregation details for later processing
          this.selectAggregations.push({
            function: aggFuncName,
            field: aggField,
            alias
          });

          // If the aggregation is not COUNT(*), ensure the field is selected for client-side aggregation
          if (aggField !== '*') {
            selectedFields.push(aggField);
          }
        } else if (col.expr.type === 'star') {
          // SELECT *
          this.selectedFields = null; // Select all fields
        } else {
          throw new Error(`Unsupported expression in SELECT clause: ${col.expr.type}`);
        }
      });

      // Handle projections based on functions and aggregations
      if (this.selectFunctions.length > 0 || this.selectAggregations.length > 0) {
        if (selectedFields.length > 0) {
          this.selectedFields = Array.from(new Set(selectedFields)); // Remove duplicates
        } else {
          this.selectedFields = null; // No specific fields needed
        }
      } else {
        // No functions or aggregations, handle projections normally
        if (!(ast.columns.length === 1 && ast.columns[0].expr.type === 'star')) {
          if (selectedFields.length === 0) {
            throw new Error('No valid columns selected for projection.');
          }
          console.log('Selected fields for projection:', selectedFields);
          this.selectedFields = selectedFields; // Store selected fields for client-side projection
        } else {
          this.selectedFields = null; // No projection needed, select all fields
        }
      }
    } else {
      this.selectedFields = null; // No columns specified, select all fields
    }

    // Log condition groups, select functions, and aggregations for debugging
    console.log('Condition Groups:', conditionGroups);
    console.log('Select Functions:', this.selectFunctions);
    console.log('Select Aggregations:', this.selectAggregations);

    return { firestoreRef, conditionGroups, commonConstraints, hasAggregation };
  }

  /**
   * Parses the WHERE clause of the SQL query.
   * Supports OR logic by splitting into condition groups.
   * @param {object} where - The WHERE clause AST node.
   * @returns {Array} - An array of arrays, each representing a group of Firestore query constraints.
   */
  parseWhere(where) {
    // Reset condition groups
    this.orConditionGroups = [];

    // Helper function to recursively traverse the AST and extract condition groups
    const traverse = (node) => {
      if (node.type === 'binary_expr' && node.operator.toUpperCase() === 'OR') {
        // When an OR operator is found, traverse left and right
        traverse(node.left);
        traverse(node.right);
      } else {
        // For non-OR nodes, collect constraints for an AND group
        const constraints = [];
        const collectConstraints = (subNode) => {
          if (subNode.type === 'binary_expr') {
            if (subNode.left.type === 'function') {
              // Handle function-based conditions (e.g., YEAR)
              // Extract the function name
              let functionName = '';

              if (
                subNode.left.name &&
                typeof subNode.left.name === 'object' &&
                Array.isArray(subNode.left.name.name) &&
                subNode.left.name.name.length > 0 &&
                subNode.left.name.name[0].value
              ) {
                functionName = subNode.left.name.name.map(part => part.value).join('.').toUpperCase();
              } else if (typeof subNode.left.name === 'string') {
                functionName = subNode.left.name.toUpperCase();
              } else {
                throw new Error('Function name is missing or invalid in WHERE clause.');
              }

              // Handle YEAR function
              if (functionName === 'YEAR') {
                // Ensure that there is exactly one argument
                if (
                  !subNode.left.args ||
                  subNode.left.args.type !== 'expr_list' ||
                  !Array.isArray(subNode.left.args.value) ||
                  subNode.left.args.value.length !== 1
                ) {
                  throw new Error('YEAR function expects exactly one argument.');
                }

                const arg = subNode.left.args.value[0];

                // Ensure the argument is a column reference
                if (!arg.column) {
                  throw new Error('YEAR function expects a column reference as its argument.');
                }

                const field = arg.column;
                const operator = this.mapOperator(subNode.operator);
                const year = subNode.right.value;

                if (operator === '==') { // Only supporting equality for YEAR
                  // Define start and end dates for the specified year
                  const startDate = new Date(year, 0, 1); // January 1st
                  const endDate = new Date(year + 1, 0, 1); // January 1st of next year

                  // Push two constraints to represent YEAR(fechaNacimiento) = 1993
                  constraints.push(firestoreWhere(field, '>=', startDate));
                  constraints.push(firestoreWhere(field, '<', endDate));
                } else {
                  throw new Error(`Operator ${operator} is not supported with YEAR function.`);
                }
              } else {
                throw new Error(`Function ${functionName} is not supported in WHERE clauses.`);
              }
            } else {
              // Handle regular field comparisons
              const field = subNode.left.column;
              const operator = this.mapOperator(subNode.operator);
              let value = subNode.right.value;

              // Parse the value based on the field type
              try {
                value = this.parseValue(field, value);
              } catch (error) {
                throw new Error(`Error parsing value for field ${field}: ${error.message}`);
              }

              console.log(`Parsed WHERE condition: ${field} ${operator} ${value}`);

              // Push the QueryConstraint directly
              constraints.push(firestoreWhere(field, operator, value));
            }
          }
          // Handle other condition types as needed
        };

        collectConstraints(node);
        this.orConditionGroups.push(constraints);
      }
    };

    traverse(where);
    console.log(`Total OR condition groups: ${this.orConditionGroups.length}`);
    return this.orConditionGroups;
  }

  /**
   * Parses the ORDER BY clause of the SQL query.
   * @param {Array} orderby - The ORDER BY clause AST nodes.
   * @returns {Array} - An array of Firestore query constraints.
   */
  parseOrderBy(orderby) {
    const orders = orderby.map(order => {
      const field = order.expr.column;
      const direction = order.type.toLowerCase(); // 'asc' or 'desc'
      console.log(`Parsed ORDER BY: ${field} ${direction}`);
      return firestoreOrderBy(field, direction);
    });
    return orders;
  }

  /**
   * Parses the LIMIT clause of the SQL query.
   * @param {object} limit - The LIMIT clause AST node.
   * @returns {object} - A Firestore limit constraint.
   */
  parseLimit(limit) {
    const limitValue = parseInt(limit.value);
    console.log(`Parsed LIMIT: ${limitValue}`);
    return firestoreLimit(limitValue);
  }

  /**
   * Maps SQL operators to Firestore operators.
   * @param {string} operator - The SQL operator.
   * @returns {string} - The corresponding Firestore operator.
   */
  mapOperator(operator) {
    const operatorMap = {
      '=': '==',
      '!=': '!=',
      '>': '>',
      '<': '<',
      '>=': '>=',
      '<=': '<=',
      // Add more mappings if necessary
    };
    const mapped = operatorMap[operator];
    if (!mapped) {
      throw new Error(`Unsupported operator: ${operator}`);
    }
    return mapped;
  }

  /**
   * Parses the value based on the field's data type.
   * @param {string} field - The field name.
   * @param {any} value - The value to parse.
   * @returns {any} - The parsed value.
   */
  parseValue(field, value) {
    const type = this.fieldTypes[field];
    if (!type) {
      // Default to string if type is not defined
      return value;
    }

    switch (type) {
      case 'date':
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          throw new Error(`Invalid date format for field ${field}: ${value}`);
        }
        return date;
      case 'number':
        const num = Number(value);
        if (isNaN(num)) {
          throw new Error(`Invalid number format for field ${field}: ${value}`);
        }
        return num;
      case 'boolean':
        return Boolean(value);
      // Add more cases as needed
      default:
        return value;
    }
  }

  /**
   * Executes a standard SELECT query without aggregations or groupings.
   * Supports OR logic by executing multiple Firestore queries and merging results.
   * Also handles client-side functions like CONCAT.
   * @param {string} sql - The SQL query string.
   * @returns {Array|object} - An array of result objects or an object containing aggregation results.
   */
  async executeQuery(sql) {
    const ast = this.parseSQL(sql);
    const { firestoreRef, conditionGroups, commonConstraints, hasAggregation } = this.translateToFirestore(ast);
    
    console.log('Executing Firestore queries for each condition group.');

    const allResults = new Set(); // To store unique document IDs
    let finalResults = []; // To store unique documents

    // Iterate over each condition group and execute queries
    for (const [index, constraints] of conditionGroups.entries()) {
      console.log(`Executing query for condition group ${index + 1}:`, constraints);
      const q = firestoreQuery(firestoreRef, ...constraints, ...commonConstraints);
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => {
        const docId = doc.id;
        if (!allResults.has(docId)) {
          allResults.add(docId);
          finalResults.push(doc.data());
        }
      });
    }

    console.log('Combined Firestore Query Snapshot:', finalResults);

    // Handle client-side projection if selectedFields is set
    if (this.selectedFields) {
      console.log('Applying client-side projection:', this.selectedFields);
      finalResults = finalResults.map(doc => {
        const projected = {};
        this.selectedFields.forEach(field => {
          projected[field] = doc[field];
        });
        return projected;
      });
    }

    // Handle client-side functions like CONCAT
    if (this.selectFunctions.length > 0) {
      console.log('Applying client-side functions:', this.selectFunctions);
      finalResults = finalResults.map(doc => {
        this.selectFunctions.forEach(func => {
          if (func.function === 'CONCAT') {
            const concatenated = func.args.map(arg => {
              if (arg.type === 'field') {
                return doc[arg.value] !== undefined && doc[arg.value] !== null ? String(doc[arg.value]) : ''; // Handle undefined/null fields
              } else if (arg.type === 'literal') {
                return arg.value;
              }
            }).join('');
            doc[func.alias] = concatenated;
          }
          // Add more functions here if needed
        });
        return doc;
      });
    }

    // Handle client-side aggregations if present
    if (this.selectAggregations.length > 0) {
      console.log('Applying client-side aggregations:', this.selectAggregations);
      const aggregationResults = this.handleAggregations(this.selectAggregations, finalResults);
      console.log('Final Aggregated Query Results:', aggregationResults);
      
      // Clear the condition groups and filters for subsequent queries
      this.orConditionGroups = [];
      this.selectFunctions = [];
      this.selectAggregations = [];

      return aggregationResults;
    }

    console.log('Final Query Results after merging, filtering, and function processing:', finalResults);

    // Clear the condition groups and filters for subsequent queries
    this.orConditionGroups = [];
    this.selectFunctions = [];
    this.selectAggregations = [];

    return finalResults;
  }

  /**
   * Executes a grouped query (e.g., GROUP BY).
   * @param {string} sql - The SQL query string.
   * @returns {object} - An object containing grouped and aggregated results.
   */
  async executeGroupedQuery(sql) {
    console.log("Entered");
    const ast = this.parseSQL(sql);
    const { firestoreRef, conditionGroups, commonConstraints, hasAggregation } = this.translateToFirestore(ast);

    if (ast.type !== 'select') {
      throw new Error('Only SELECT statements are supported');
    }
    
    console.log("This has been executed");
    console.log(ast.groupby);
    // Check for GROUP BY
    if (!ast.groupby || ast.groupby.length === 0) {
      throw new Error('GROUP BY clause is missing');
    }

    const groupByField = ast.groupby.columns[0].column;
    const selectColumns = ast.columns;

    // Execute Firestore queries for each condition group
    console.log('Executing Firestore queries for each condition group in GROUP BY.');
    
    const allResults = new Set(); // To store unique document IDs
    let finalResults = []; // To store unique documents

    for (const [index, constraints] of conditionGroups.entries()) {
      console.log(`Executing query for condition group ${index + 1}:`, constraints);
      const q = firestoreQuery(firestoreRef, ...constraints, ...commonConstraints);
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => {
        const docId = doc.id;
        if (!allResults.has(docId)) {
          allResults.add(docId);
          finalResults.push(doc.data());
        }
      });
    }

    console.log('Combined Firestore Query Snapshot:', finalResults);

    // Handle client-side projection if selectedFields is set
    if (this.selectedFields) {
      console.log('Applying client-side projection:', this.selectedFields);
      finalResults = finalResults.map(doc => {
        const projected = {};
        this.selectedFields.forEach(field => {
          projected[field] = doc[field];
        });
        return projected;
      });
    }

    // Handle client-side functions like CONCAT
    if (this.selectFunctions.length > 0) {
      console.log('Applying client-side functions:', this.selectFunctions);
      finalResults = finalResults.map(doc => {
        this.selectFunctions.forEach(func => {
          if (func.function === 'CONCAT') {
            const concatenated = func.args.map(arg => {
              if (arg.type === 'field') {
                return doc[arg.value] !== undefined && doc[arg.value] !== null ? String(doc[arg.value]) : ''; // Handle undefined/null fields
              } else if (arg.type === 'literal') {
                return arg.value;
              }
            }).join('');
            doc[func.alias] = concatenated;
          }
          // Add more functions here if needed
        });
        return doc;
      });
    }

    // Group the results by the specified field
    const grouped = this.groupBy(finalResults, groupByField);
    console.log('Grouped Data:', grouped);

    // Apply aggregations per group
    const aggregatedResults = {};
    for (const [group, items] of Object.entries(grouped)) {
      aggregatedResults[group] = {};
      selectColumns.forEach(col => {
        if (col.expr.type === 'aggr_func' || col.expr.type === 'aggregate_func') {
          const aggFuncName = this.extractFunctionName(col.expr.name);
          const func = aggFuncName.toLowerCase();
          const field = (col.expr.args && col.expr.args.expr && col.expr.args.expr.type === 'star') ? '*' : (col.expr.args.expr.column || '*');
          const alias = col.as || `${func}_${field}`;

          switch (func) {
            case 'count':
              if (field === '*') {
                aggregatedResults[group][alias] = items.length;
              } else {
                aggregatedResults[group][alias] = items.filter(doc => doc[field] !== undefined && doc[field] !== null).length;
              }
              break;
            case 'sum':
              aggregatedResults[group][alias] = items.reduce((sum, doc) => sum + (Number(doc[field]) || 0), 0);
              break;
            case 'avg':
              aggregatedResults[group][alias] = items.length > 0 
                ? items.reduce((sum, doc) => sum + (Number(doc[field]) || 0), 0) / items.length 
                : 0;
              break;
            // Add more aggregation functions as needed
            default:
              aggregatedResults[group][alias] = null;
              break;
          }
        } else if (col.expr.type === 'column_ref') {
          // Include the groupBy field
          aggregatedResults[group][col.expr.column] = group;
        }
      });
    }

    console.log('Grouped and Aggregated Results:', aggregatedResults);
    return aggregatedResults;
  }

  /**
   * Handles aggregations based on the SELECT columns.
   * Not used anymore; can be removed if executeGroupedQuery handles all aggregations.
   * @param {Array} aggregations - The aggregation functions to apply.
   * @param {Array} results - The fetched Firestore documents.
   * @returns {object} - An object containing aggregation results.
   */
  handleAggregations(aggregations, results) {
    const aggregationResults = {};

    aggregations.forEach(agg => {
      const { function: func, field, alias } = agg;
      switch (func) {
        case 'COUNT':
          if (field === '*') {
            aggregationResults[alias] = results.length;
          } else {
            aggregationResults[alias] = results.filter(doc => doc[field] !== undefined && doc[field] !== null).length;
          }
          break;
        case 'SUM':
          aggregationResults[alias] = results.reduce((sum, doc) => sum + (Number(doc[field]) || 0), 0);
          break;
        case 'AVG':
          aggregationResults[alias] = results.length > 0 
            ? results.reduce((sum, doc) => sum + (Number(doc[field]) || 0), 0) / results.length 
            : 0;
          break;
        // Add more aggregation functions as needed
        default:
          aggregationResults[alias] = null;
          break;
      }
    });

    return aggregationResults;
  }

  /**
   * Groups an array of objects by a specified key.
   * @param {Array} array - The array of objects to group.
   * @param {string} key - The key to group by.
   * @returns {object} - An object where each key is a group and the value is an array of items.
   */
  groupBy(array, key) {
    return array.reduce((result, current) => {
      const groupKey = current[key];
      if (groupKey === undefined || groupKey === null) {
        console.warn(`Document is missing the groupBy field '${key}':`, current);
        return result;
      }
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(current);
      return result;
    }, {});
  }
}

export default SQLFirestore;
